import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, Clock, Gift, Tag, Store, ImageOff, MapPin, CheckCircle2, AlertTriangle } from 'lucide-react';
import api, { fileUrl } from '@/lib/api';
import { toast } from 'sonner';

const META = {
  free: { label: 'Free', color: '#7A9A78', Icon: Gift },
  yard_sale: { label: 'Yard Sale', color: '#D99E4A', Icon: Store },
};

function fmtDate(iso) { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }

function PinRow({ p, onSelect, onDelete, onConfirm }) {
  const meta = META[p.category];
  const Icon = meta.Icon;
  const status = p.status;
  const STATUS_COLORS = { active: '#7A9A78', upcoming: '#D99E4A', hidden: '#C86F53' };
  const statusColor = STATUS_COLORS[status] || '#8A8F87';
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white/70 p-3 hover:bg-white transition" data-testid={`my-pin-row-${p.id}`}>
      <div className="w-14 h-14 rounded-xl overflow-hidden bg-black/5 shrink-0">
        {p.photos?.[0] ? <img src={fileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center"><ImageOff className="w-4 h-4 text-brand-dim" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest" style={{ background: meta.color + '22', color: meta.color }}>
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: statusColor + '22', color: statusColor }}>
            {status === 'hidden' ? <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-0.5" /> : null}
            {status}
          </span>
          <span className="text-[11px] text-brand-slate"><Clock className="w-3 h-3 inline -mt-0.5 mr-0.5" />{fmtDate(p.starts_at)} → {fmtDate(p.ends_at)}</span>
        </div>
        <div className="font-display font-semibold text-base truncate" title={p.title}>{p.title}</div>
      </div>
      {status === 'hidden' && onConfirm && (
        <button data-testid={`confirm-pin-${p.id}`} onClick={() => onConfirm(p.id)} title="It's still here" className="p-2 rounded-full hover:bg-[#7A9A78]/10">
          <CheckCircle2 className="w-4 h-4 text-[#7A9A78]" />
        </button>
      )}
      {onSelect && (
        <button data-testid={`view-on-map-${p.id}`} onClick={() => onSelect(p)} title="View on map" className="p-2 rounded-full hover:bg-black/5">
          <MapPin className="w-4 h-4 text-brand-ink" />
        </button>
      )}
      <button data-testid={`delete-my-pin-${p.id}`} onClick={() => onDelete(p.id)} title="Delete" className="p-2 rounded-full hover:bg-[#C86F53]/10">
        <Trash2 className="w-4 h-4 text-[#C86F53]" />
      </button>
    </li>
  );
}

export default function MyPinsDialog({ open, onOpenChange, onSelect, onChanged }) {
  const [tab, setTab] = useState('active');
  const [active, setActive] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([api.get('/me/pins'), api.get('/me/pins/past')]);
      setActive(a.data); setPast(p.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pin?')) return;
    try {
      await api.delete(`/pins/${id}`);
      setActive(p => p.filter(x => x.id !== id));
      setPast(p => p.filter(x => x.id !== id));
      toast.success('Pin deleted');
      onChanged && onChanged();
    } catch { toast.error('Could not delete'); }
  };

  const handleConfirm = async (id) => {
    try {
      const r = await api.post(`/pins/${id}/confirm`);
      setActive(prev => prev.map(p => p.id === id ? r.data : p));
      toast.success('Pin is visible again');
      onChanged && onChanged();
    } catch { toast.error('Could not confirm'); }
  };

  const list = tab === 'active' ? active : past;
  const hiddenCount = active.filter(p => p.status === 'hidden').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg p-0 overflow-hidden glass-panel" data-testid="my-pins-dialog">
        <div className="p-6 sm:p-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">My Pins</DialogTitle>
            <DialogDescription className="text-brand-slate">Track your pins. Past pins are kept for 2 weeks.</DialogDescription>
          </DialogHeader>

          {hiddenCount > 0 && tab === 'active' && (
            <div className="mt-4 rounded-2xl p-3 bg-[#C86F53]/10 border border-[#C86F53]/20 text-xs text-[#C86F53] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {hiddenCount} pin{hiddenCount > 1 ? 's' : ''} reported as gone — confirm or they'll be archived in 24h.
            </div>
          )}

          <div className="mt-5 inline-flex rounded-full bg-black/5 p-1">
            <button data-testid="tab-active" onClick={() => setTab('active')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${tab === 'active' ? 'bg-white shadow text-brand-ink' : 'text-brand-slate'}`}>
              Active <span className="text-xs opacity-70">({active.length})</span>
            </button>
            <button data-testid="tab-past" onClick={() => setTab('past')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${tab === 'past' ? 'bg-white shadow text-brand-ink' : 'text-brand-slate'}`}>
              Past Pins <span className="text-xs opacity-70">({past.length})</span>
            </button>
          </div>

          <div className="mt-4 max-h-[60vh] overflow-y-auto scroll-soft pr-1 -mr-1">
            {loading && <div className="py-10 text-center text-brand-slate text-sm">Loading…</div>}
            {!loading && list.length === 0 && (
              <div className="py-10 text-center text-brand-slate text-sm">
                {tab === 'active' ? 'No active pins yet. Drop one on the map!' : 'No past pins from the last 2 weeks.'}
              </div>
            )}
            <ul className="space-y-3">
              {list.map(p => (
                <PinRow key={p.id} p={p}
                  onSelect={tab === 'active' ? (pin) => { onSelect && onSelect(pin); onOpenChange(false); } : null}
                  onDelete={handleDelete}
                  onConfirm={tab === 'active' ? handleConfirm : null}
                />
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
